import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, Loader2, Download, CheckCircle2, XCircle, Send, Link2, RefreshCw,
  FileText, Trash2, ExternalLink, ChevronRight, Eye, MousePointer, FileCheck, Sparkles, X,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { formatIndianDate, formatCurrencyINR } from "../utils/format";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABEL = {
  draft: { label: "Draft", bg: "bg-slate-100 text-slate-700 border-slate-200" },
  sent: { label: "Sent", bg: "bg-blue-50 text-blue-700 border-blue-200" },
  viewed: { label: "Viewed by Client", bg: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  pending_response: { label: "Pending Response", bg: "bg-amber-50 text-amber-700 border-amber-200" },
  self_approved: { label: "Self Approved", bg: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  client_approved_registered: { label: "Approved by Client (Registered)", bg: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  client_approved_invited: { label: "Approved by Client (Invited)", bg: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  seller_marked_approved: { label: "Seller Marked Approved", bg: "bg-amber-100 text-amber-800 border-amber-200" },
  rejected: { label: "Rejected", bg: "bg-red-50 text-red-700 border-red-200" },
  revision_requested: { label: "Revision Requested", bg: "bg-orange-50 text-orange-700 border-orange-200" },
  converted_to_invoice: { label: "Converted to Invoice", bg: "bg-violet-100 text-violet-800 border-violet-200" },
  expired: { label: "Expired", bg: "bg-slate-100 text-slate-500 border-slate-200" },
};

// Timeline stages — mapped from current status to a consistent SMB-friendly journey
const APPROVED_STATUSES = ["self_approved", "client_approved_registered", "client_approved_invited", "seller_marked_approved"];
const stageFor = (status) => {
  if (status === "draft") return 0;
  if (status === "sent") return 1;
  if (status === "viewed" || status === "pending_response") return 2;
  if (APPROVED_STATUSES.includes(status)) return 3;
  if (status === "converted_to_invoice") return 4;
  return 0;
};

export default function QuotationDetailPage() {
  const { quotationId } = useParams();
  const navigate = useNavigate();
  const [q, setQ] = useState(null);
  const [financial, setFinancial] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [showConvert, setShowConvert] = useState(false);

  useEffect(() => { fetchQ(); }, [quotationId]); // eslint-disable-line

  const fetchQ = async () => {
    try {
      const [a, b] = await Promise.all([
        axios.get(`${API}/quotations/${quotationId}`, { withCredentials: true }),
        axios.get(`${API}/quotations/${quotationId}/financial`, { withCredentials: true }).catch(() => ({ data: null })),
      ]);
      setQ(a.data);
      setFinancial(b.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load");
      navigate("/quotations");
    } finally { setLoading(false); }
  };

  const updateStatus = async (status, msg) => {
    setActing(true);
    try {
      await axios.post(`${API}/quotations/${quotationId}/status`, { status }, { withCredentials: true });
      toast.success(msg);
      fetchQ();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setActing(false); }
  };

  const setApprovalMode = async (mode) => {
    setActing(true);
    try {
      await axios.post(`${API}/quotations/${quotationId}/approval-mode`, { mode }, { withCredentials: true });
      toast.success(mode === "self" ? "Switched to Self-sign mode" : "Switched to Client approval mode");
      fetchQ();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to set mode");
    } finally { setActing(false); }
  };

  const download = async () => {
    try {
      const res = await axios.get(`${API}/quotations/${quotationId}/pdf`, { withCredentials: true, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${q.quotation_number.replace(/\//g, "-")}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Failed to download PDF");
    }
  };

  const copyShareLink = () => {
    const url = `${window.location.origin}/quote/${q.share_token}`;
    navigator.clipboard.writeText(url);
    toast.success("Share link copied! Send it to your client to approve.");
  };

  const deleteDraft = async () => {
    if (!window.confirm("Delete this quotation? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/quotations/${quotationId}`, { withCredentials: true });
      toast.success("Quotation deleted");
      navigate("/quotations");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    }
  };

  if (loading) return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;
  if (!q) return null;

  const st = STATUS_LABEL[q.status] || STATUS_LABEL.draft;
  const isDraft = q.status === "draft";
  const isConverted = q.status === "converted_to_invoice";
  const fullyInvoiced = financial?.milestone_status === "fully_invoiced";
  const canConvert = !fullyInvoiced && !["rejected", "expired"].includes(q.status);
  const canDelete = ["draft", "rejected", "expired"].includes(q.status);
  const currentStage = stageFor(q.status);

  return (
    <div className="min-h-screen bg-slate-50 py-6 sm:py-8 px-4" data-testid="quotation-detail-page">
      <div className="max-w-5xl mx-auto">
        <Link to="/quotations" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900 mb-4">
          <ArrowLeft className="w-4 h-4" /> Back
        </Link>

        {/* === Workflow Timeline (P0 — first-time clarity) === */}
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 sm:p-6 mb-5"
          data-testid="q-workflow-stepper"
        >
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Quotation Workflow</p>
            <button
              onClick={copyShareLink}
              className="text-[11px] inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 font-semibold"
              data-testid="q-stepper-copy-link"
            >
              <Link2 className="w-3 h-3" /> Copy share link
            </button>
          </div>
          <Stepper currentStage={currentStage} status={q.status} approvalMode={q.approval_mode} />
          <p className="text-xs text-slate-500 mt-4 leading-relaxed" data-testid="q-stepper-helper">
            {currentStage === 0 && "👋 Save your quotation as a draft, then click ‘Mark Sent’ once you’ve emailed/WhatsApp’d the share link to your client."}
            {currentStage === 1 && !q.approval_mode && "Quotation marked as sent. Choose how this will be approved below — by you (self-sign) or by the client (share link)."}
            {currentStage === 1 && q.approval_mode === "self" && "Self-sign mode — review the line items and click ‘Mark Approved’ when you’re ready to move on."}
            {currentStage === 1 && q.approval_mode === "client" && "Client-sign mode — share the link with your client. We’ll auto-mark this ‘Viewed’ the moment they open it."}
            {currentStage === 2 && q.approval_mode === "self" && "You’re tracking client engagement — they viewed it. Click ‘Mark Approved’ to lock the quote."}
            {currentStage === 2 && q.approval_mode !== "self" && "Your client has opened the quote. They can approve via the link, or you can record their decision below using ‘Mark Approved’."}
            {currentStage === 3 && "Approved! Click ‘Convert to Invoice’ — you’ll be asked to bill the full amount or just a milestone slice."}
            {currentStage === 4 && fullyInvoiced && "All done — this quotation is fully invoiced."}
            {q.status === "rejected" && "This quotation was rejected. You can clone it from the list page if you want to revise."}
            {q.status === "revision_requested" && "Client requested changes. Edit the items if needed, then re-share the link."}
          </p>

          {/* ── Approval Mode picker + primary CTA — placed directly under timeline for clarity ── */}
          {!isConverted && !["rejected", "expired"].includes(q.status) && !APPROVED_STATUSES.includes(q.status) && (
            <div className="mt-5 pt-5 border-t border-slate-100" data-testid="q-approval-panel">
              {/* Mode picker — appears once status >= sent */}
              {currentStage >= 1 && (
                <>
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-2.5">
                    <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                      Who will approve this quotation?
                    </p>
                    {q.approval_mode && (
                      <span className="text-[10px] font-semibold text-blue-700 bg-blue-50 border border-blue-200 px-2 py-0.5 rounded-full" data-testid="q-mode-active-pill">
                        {q.approval_mode === "self" ? "Self-sign" : "Client to sign"} · active
                      </span>
                    )}
                  </div>
                  <div className="grid sm:grid-cols-2 gap-2 mb-4">
                    <button
                      type="button"
                      onClick={() => setApprovalMode("self")}
                      disabled={acting}
                      data-testid="q-mode-self"
                      className={`text-left p-3 rounded-xl border transition-colors ${
                        q.approval_mode === "self"
                          ? "border-emerald-300 bg-emerald-50/60"
                          : "border-slate-200 hover:border-slate-300 bg-white"
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <CheckCircle2 className={`w-4 h-4 ${q.approval_mode === "self" ? "text-emerald-600" : "text-slate-400"}`} />
                        <span className="text-sm font-semibold text-slate-900">Self-sign (you approve)</span>
                      </div>
                      <p className="text-[11px] text-slate-500">You'll mark this approved manually — best when client confirms over phone / WhatsApp.</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => setApprovalMode("client")}
                      disabled={acting}
                      data-testid="q-mode-client"
                      className={`text-left p-3 rounded-xl border transition-colors ${
                        q.approval_mode === "client"
                          ? "border-blue-300 bg-blue-50/60"
                          : "border-slate-200 hover:border-slate-300 bg-white"
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <Send className={`w-4 h-4 ${q.approval_mode === "client" ? "text-blue-600" : "text-slate-400"}`} />
                        <span className="text-sm font-semibold text-slate-900">Client to approve</span>
                      </div>
                      <p className="text-[11px] text-slate-500">Send the share link — your client clicks Approve / Reject / Request changes themselves.</p>
                    </button>
                  </div>
                </>
              )}

              {/* Primary CTAs — switch based on mode */}
              <div className="flex flex-wrap items-center gap-2" data-testid="q-primary-ctas">
                {isDraft && (
                  <Button onClick={() => updateStatus("sent", "Marked as sent — choose approval mode below")} disabled={acting} className="bg-blue-600 text-white hover:bg-blue-700 text-sm" data-testid="q-cta-mark-sent">
                    <Send className="w-4 h-4 mr-1" /> Mark as Sent
                  </Button>
                )}
                {!isDraft && q.approval_mode === "self" && (
                  <Button onClick={() => updateStatus("self_approved", "Marked self-approved")} disabled={acting} className="bg-emerald-600 text-white hover:bg-emerald-700 text-sm" data-testid="q-cta-self-approve">
                    <CheckCircle2 className="w-4 h-4 mr-1" /> Mark Approved (Self-sign)
                  </Button>
                )}
                {!isDraft && q.approval_mode === "client" && (
                  <>
                    <Button onClick={copyShareLink} className="bg-blue-600 text-white hover:bg-blue-700 text-sm" data-testid="q-cta-copy-link">
                      <Link2 className="w-4 h-4 mr-1" /> Copy share link
                    </Button>
                    <Button onClick={() => updateStatus("seller_marked_approved", "Marked approved on client's behalf")} disabled={acting} variant="outline" className="text-sm border-amber-200 text-amber-700 hover:bg-amber-50" data-testid="q-cta-mark-on-behalf">
                      <CheckCircle2 className="w-4 h-4 mr-1" /> Mark Approved (on client's behalf)
                    </Button>
                  </>
                )}
                {!isDraft && !q.approval_mode && (
                  <p className="text-xs text-slate-500 italic" data-testid="q-cta-pick-mode-hint">
                    Pick an approval mode above to reveal next steps.
                  </p>
                )}
              </div>
            </div>
          )}
        </motion.div>

        {/* === Header card with actions === */}
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 sm:p-6 mb-5"
        >
          <div className="flex flex-col sm:flex-row gap-4 justify-between items-start">
            <div className="flex gap-3 items-start">
              <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0">
                <FileText className="w-6 h-6" />
              </div>
              <div>
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <h1 className="text-xl sm:text-2xl font-bold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="q-number">{q.quotation_number}</h1>
                  <span className={`text-[11px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border ${st.bg}`} data-testid="q-status-badge">{st.label}</span>
                </div>
                <p className="text-sm text-slate-500">
                  <span className="font-medium">Quotation Date:</span> {formatIndianDate(q.quotation_date)}
                  {q.valid_until && <> · <span className="font-medium">Valid Until:</span> {formatIndianDate(q.valid_until)}</>}
                </p>
                <p className="text-sm text-slate-700 mt-1"><span className="font-medium">Client:</span> {q.customer_name}</p>
                {q.converted_invoice_id && (
                  <Link to={`/invoices/${q.converted_invoice_id}`} className="inline-flex items-center gap-1 mt-1 text-xs text-violet-700 hover:underline" data-testid="q-converted-link">
                    Converted to invoice <ExternalLink className="w-3 h-3" />
                  </Link>
                )}
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button onClick={download} variant="outline" className="text-sm" data-testid="q-download">
                <Download className="w-4 h-4 mr-1" /> Download PDF
              </Button>
              <Button onClick={copyShareLink} variant="outline" className="text-sm" data-testid="q-copy-link">
                <Link2 className="w-4 h-4 mr-1" /> Copy share link
              </Button>
              {isDraft && (
                <Button onClick={() => updateStatus("sent", "Marked as sent — share the link with your client")} disabled={acting} className="bg-blue-600 text-white hover:bg-blue-700 text-sm" data-testid="q-mark-sent">
                  <Send className="w-4 h-4 mr-1" /> Mark Sent
                </Button>
              )}
              {!isConverted && !["rejected", "expired"].includes(q.status) && !APPROVED_STATUSES.includes(q.status) && (
                <Button onClick={() => updateStatus("seller_marked_approved", "Marked approved by you")} disabled={acting} variant="outline" className="text-sm border-amber-200 text-amber-700 hover:bg-amber-50" data-testid="q-seller-approve">
                  <CheckCircle2 className="w-4 h-4 mr-1" /> Mark Approved
                </Button>
              )}
              {!isConverted && q.status !== "rejected" && (
                <Button onClick={() => updateStatus("rejected", "Marked rejected")} disabled={acting} variant="outline" className="text-sm border-red-200 text-red-600 hover:bg-red-50" data-testid="q-reject">
                  <XCircle className="w-4 h-4 mr-1" /> Reject
                </Button>
              )}
              {canConvert && (
                <Button onClick={() => setShowConvert(true)} disabled={acting} className="bg-emerald-600 text-white hover:bg-emerald-700 text-sm" data-testid="q-convert">
                  <RefreshCw className="w-4 h-4 mr-1" /> Convert to Invoice
                </Button>
              )}
              {canDelete && (
                <Button onClick={deleteDraft} variant="outline" className="text-sm border-red-200 text-red-600 hover:bg-red-50" data-testid="q-delete">
                  <Trash2 className="w-4 h-4 mr-1" /> Delete
                </Button>
              )}
            </div>
          </div>
        </motion.div>

        {/* Milestone billing — financial summary + linked invoices */}
        {financial && (
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 mb-5" data-testid="q-milestone-card">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
              <div>
                <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Milestone Billing</p>
                <p className="text-sm text-slate-700 mt-0.5">
                  Quote total <strong>{formatCurrencyINR(financial.quotation_value)}</strong> · {financial.linked_invoice_count} invoice(s) linked
                </p>
              </div>
              <span className={`text-[11px] font-semibold uppercase tracking-wider px-2 py-1 rounded-full ${
                financial.milestone_status === "fully_invoiced" ? "bg-emerald-100 text-emerald-700"
                : financial.milestone_status === "partially_invoiced" ? "bg-blue-50 text-blue-700"
                : financial.milestone_status === "cancelled" ? "bg-slate-100 text-slate-500"
                : "bg-amber-50 text-amber-700"
              }`} data-testid="q-milestone-status">
                {{
                  open: "Open", partially_invoiced: "Partially Invoiced",
                  fully_invoiced: "Fully Invoiced", cancelled: "Cancelled",
                }[financial.milestone_status] || financial.milestone_status}
              </span>
            </div>

            <div className="h-2 bg-slate-100 rounded-full overflow-hidden mb-2" data-testid="q-progress-track">
              <div className="h-full bg-gradient-to-r from-blue-500 to-emerald-500 transition-all duration-500" style={{ width: `${Math.min(100, financial.pct_invoiced)}%` }} data-testid="q-progress-fill" />
            </div>
            <p className="text-xs text-slate-500 mb-4">
              {formatCurrencyINR(financial.total_invoiced)} invoiced of {formatCurrencyINR(financial.quotation_value)} ({financial.pct_invoiced}% completed)
            </p>

            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-center mb-5">
              <Metric label="Quote Value" value={formatCurrencyINR(financial.quotation_value)} testid="m-quote-value" />
              <Metric label="Invoiced" value={formatCurrencyINR(financial.total_invoiced)} testid="m-invoiced" tone="blue" />
              <Metric label="Paid" value={formatCurrencyINR(financial.total_paid)} testid="m-paid" tone="emerald" />
              <Metric label="Pending Payment" value={formatCurrencyINR(financial.pending_payment)} testid="m-pending" tone="amber" />
              <Metric label="Remaining To Invoice" value={formatCurrencyINR(financial.remaining_to_invoice)} testid="m-remaining" tone="violet" />
            </div>

            {financial.timeline.length > 0 ? (
              <div data-testid="q-linked-invoices">
                <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Linked invoices ({financial.timeline.length})</p>
                <div className="space-y-2">
                  {financial.timeline.map((t, i) => {
                    const remainingAfter = Math.max(0, financial.quotation_value - financial.timeline.slice(0, i + 1).reduce((s, r) => s + r.allocated_amount, 0));
                    const payBg = t.payment_status === "paid" ? "bg-emerald-50 text-emerald-700"
                      : t.payment_status === "partial" ? "bg-amber-50 text-amber-700"
                      : "bg-red-50 text-red-700";
                    return (
                      <Link
                        key={t.invoice_id}
                        to={`/invoices/${t.invoice_id}`}
                        className="flex flex-wrap items-center gap-3 px-3 py-2 rounded-xl border border-slate-100 hover:border-slate-200 hover:bg-slate-50/50 transition-colors"
                        data-testid={`q-linked-inv-${i}`}
                      >
                        <span className="w-7 h-7 rounded-full bg-blue-50 text-blue-600 text-xs font-bold flex items-center justify-center flex-shrink-0">{i + 1}</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-slate-900 truncate">{t.invoice_number}</p>
                          <p className="text-xs text-slate-500">{formatIndianDate(t.invoice_date)} · Allocated {formatCurrencyINR(t.allocated_amount)} · Remaining after this: {formatCurrencyINR(remainingAfter)}</p>
                        </div>
                        <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${payBg}`}>{t.payment_status}</span>
                        <span className="text-sm font-bold text-slate-900">{formatCurrencyINR(t.grand_total)}</span>
                      </Link>
                    );
                  })}
                </div>
              </div>
            ) : (
              <p className="text-xs text-slate-500" data-testid="q-no-invoices">No invoices linked yet. Click ‘Convert to Invoice’ above to bill the full amount or a milestone slice.</p>
            )}
          </div>
        )}

        {/* Items */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden mb-5">
          <div className="p-5 border-b border-slate-100">
            <h3 className="text-sm font-semibold text-slate-900">Line Items</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-100">
                <tr>
                  <th className="text-left px-4 py-2.5 text-xs font-medium text-slate-500 uppercase tracking-wider">Item</th>
                  <th className="text-right px-4 py-2.5 text-xs font-medium text-slate-500 uppercase tracking-wider">Qty</th>
                  <th className="text-right px-4 py-2.5 text-xs font-medium text-slate-500 uppercase tracking-wider">Rate</th>
                  <th className="text-right px-4 py-2.5 text-xs font-medium text-slate-500 uppercase tracking-wider">GST</th>
                  <th className="text-right px-4 py-2.5 text-xs font-medium text-slate-500 uppercase tracking-wider">Total</th>
                </tr>
              </thead>
              <tbody>
                {(q.items || []).map((it, i) => {
                  const base = (Number(it.quantity) || 0) * (Number(it.unit_price) || 0);
                  const total = it.gst_not_applicable ? base : base * (1 + (Number(it.gst_percent) || 0) / 100);
                  return (
                    <tr key={i} className="border-b border-slate-50 last:border-0" data-testid={`q-detail-item-${i}`}>
                      <td className="px-4 py-3">
                        <p className="font-medium text-slate-900">{it.name}</p>
                        {it.description && <p className="text-xs text-slate-500 mt-0.5">{it.description}</p>}
                        {it.hsn_code && <p className="text-[11px] text-slate-400 font-mono mt-0.5">HSN {it.hsn_code}</p>}
                      </td>
                      <td className="px-4 py-3 text-right text-slate-700">{it.quantity}</td>
                      <td className="px-4 py-3 text-right text-slate-700">{formatCurrencyINR(it.unit_price)}</td>
                      <td className="px-4 py-3 text-right text-slate-700">{it.gst_not_applicable ? "—" : `${it.gst_percent}%`}</td>
                      <td className="px-4 py-3 text-right font-semibold text-slate-900">{formatCurrencyINR(total)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="bg-slate-50 border-t border-slate-100 p-5">
            <div className="ml-auto max-w-xs space-y-1.5 text-sm">
              <div className="flex justify-between text-slate-600"><span>Subtotal</span><span>{formatCurrencyINR(q.subtotal)}</span></div>
              {q.tax_amount > 0 && <div className="flex justify-between text-slate-600"><span>GST</span><span>{formatCurrencyINR(q.tax_amount)}</span></div>}
              <div className="flex justify-between pt-2 border-t border-slate-200 text-base font-bold text-slate-900" data-testid="q-grand-total">
                <span>Grand Total</span><span>{formatCurrencyINR(q.grand_total)}</span>
              </div>
            </div>
          </div>
        </div>

        {(q.notes || q.terms) && (
          <div className="grid sm:grid-cols-2 gap-5 mb-5">
            {q.notes && (
              <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
                <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Notes</p>
                <p className="text-sm text-slate-700 whitespace-pre-line">{q.notes}</p>
              </div>
            )}
            {q.terms && (
              <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
                <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Terms &amp; Conditions</p>
                <p className="text-sm text-slate-700 whitespace-pre-line">{q.terms}</p>
              </div>
            )}
          </div>
        )}

        {/* History */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 mb-5">
          <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Activity Trail</p>
          <ul className="space-y-2">
            {(q.history || []).map((h, i) => (
              <li key={i} className="text-xs text-slate-600 flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1.5 flex-shrink-0" />
                <span>
                  <span className="font-semibold text-slate-800">{STATUS_LABEL[h.status]?.label || h.status?.replace(/_/g, " ")}</span>
                  {" "}— {formatIndianDate(h.at)}
                  {h.amount && <> · {formatCurrencyINR(h.amount)}</>}
                  {h.note && <> · <span className="italic text-slate-500">{h.note}</span></>}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Convert dialog */}
      <AnimatePresence>
        {showConvert && (
          <ConvertDialog
            q={q}
            financial={financial}
            onClose={() => setShowConvert(false)}
            onSuccess={(invoiceId) => navigate(`/invoices/${invoiceId}`)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// ============= Sub-components =============

function Stepper({ currentStage, status, approvalMode }) {
  const approvedLabel =
    approvalMode === "self" ? "Self Approved"
    : approvalMode === "client" ? "Client Approved"
    : "Approved";
  const approvedSub =
    approvalMode === "self" ? "You signed off"
    : approvalMode === "client" ? "Client signed off"
    : "Decision recorded";
  const stages = [
    { label: "Draft", icon: FileText, sub: "Created" },
    { label: "Sent", icon: Send, sub: "Link shared" },
    { label: "Viewed", icon: Eye, sub: "Client opened" },
    { label: approvedLabel, icon: FileCheck, sub: approvedSub },
    { label: "Invoiced", icon: Sparkles, sub: "Cash flowing" },
  ];
  const isRejected = status === "rejected";
  const isRevision = status === "revision_requested";
  return (
    <div className="flex items-start gap-1 sm:gap-2 overflow-x-auto pb-1" data-testid="q-stepper-row">
      {stages.map((s, i) => {
        const reached = i <= currentStage && !isRejected;
        const active = i === currentStage && !isRejected;
        const Icon = s.icon;
        return (
          <div key={i} className="flex items-start gap-1 sm:gap-2 flex-1 min-w-[88px]">
            <div className="flex flex-col items-center flex-1">
              <div
                className={`w-9 h-9 rounded-full flex items-center justify-center mb-1 transition-all ${
                  active ? "bg-blue-600 text-white shadow-md ring-4 ring-blue-100" :
                  reached ? "bg-emerald-500 text-white" :
                  "bg-slate-100 text-slate-400"
                }`}
                data-testid={`q-stepper-${i}-${active ? "active" : reached ? "done" : "pending"}`}
              >
                <Icon className="w-4 h-4" />
              </div>
              <p className={`text-[11px] font-semibold text-center ${active ? "text-blue-700" : reached ? "text-emerald-700" : "text-slate-400"}`}>{s.label}</p>
              <p className="text-[10px] text-slate-400 text-center hidden sm:block">{s.sub}</p>
            </div>
            {i < stages.length - 1 && (
              <ChevronRight className={`w-4 h-4 mt-2 flex-shrink-0 ${reached && i < currentStage ? "text-emerald-400" : "text-slate-200"}`} />
            )}
          </div>
        );
      })}
      {isRejected && (
        <div className="flex flex-col items-center px-2 ml-1 sm:ml-2 min-w-[80px]">
          <div className="w-9 h-9 rounded-full bg-red-500 text-white flex items-center justify-center mb-1"><XCircle className="w-4 h-4" /></div>
          <p className="text-[11px] font-semibold text-red-700 text-center">Rejected</p>
        </div>
      )}
      {isRevision && (
        <div className="flex flex-col items-center px-2 ml-1 sm:ml-2 min-w-[80px]">
          <div className="w-9 h-9 rounded-full bg-orange-500 text-white flex items-center justify-center mb-1"><MousePointer className="w-4 h-4" /></div>
          <p className="text-[11px] font-semibold text-orange-700 text-center">Revision</p>
        </div>
      )}
    </div>
  );
}

function ConvertDialog({ q, financial, onClose, onSuccess }) {
  const [mode, setMode] = useState("full");  // 'full' | 'partial_amount'
  const [amount, setAmount] = useState("");
  const [label, setLabel] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const remaining = financial?.remaining_to_invoice ?? q.grand_total;

  const handleConvert = async () => {
    if (mode === "partial_amount") {
      const amt = parseFloat(amount);
      if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
      if (amt > remaining + 0.01) { toast.error(`Amount exceeds remaining ${formatCurrencyINR(remaining)}`); return; }
    }
    setSubmitting(true);
    try {
      const body = mode === "full"
        ? { mode: "full" }
        : { mode: "partial_amount", amount: parseFloat(amount), label: label.trim() || `Milestone ${(financial?.linked_invoice_count || 0) + 1}` };
      const res = await axios.post(`${API}/quotations/${q.quotation_id}/convert`, body, { withCredentials: true });
      toast.success(`Invoice ${res.data.invoice_number} created${res.data.fully_invoiced ? "" : ` · ${formatCurrencyINR(res.data.remaining_to_invoice)} remaining on quote`}`);
      onSuccess(res.data.invoice_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to convert");
    } finally { setSubmitting(false); }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center px-4 z-50"
      onClick={onClose}
      data-testid="q-convert-dialog"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96, y: 8 }}
        onClick={e => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6"
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>Convert to Invoice</h3>
            <p className="text-xs text-slate-500 mt-0.5">{q.quotation_number} · {formatCurrencyINR(remaining)} remaining to invoice</p>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-slate-100"><X className="w-5 h-5 text-slate-400" /></button>
        </div>

        <div className="space-y-3 mb-5">
          <div
            onClick={() => setMode("full")}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setMode("full"); }}
            className={`w-full text-left p-4 rounded-xl border-2 transition-all cursor-pointer ${mode === "full" ? "border-blue-500 bg-blue-50/50" : "border-slate-100 hover:border-slate-200"}`}
            data-testid="q-convert-mode-full"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold text-slate-900 text-sm">Single Invoice (Full Amount)</p>
                <p className="text-xs text-slate-500 mt-0.5">Bill the entire remaining quote in one invoice. Best for one-shot deliveries.</p>
              </div>
              <span className="text-base font-bold text-slate-900 ml-3">{formatCurrencyINR(remaining)}</span>
            </div>
          </div>

          <div
            onClick={() => setMode("partial_amount")}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setMode("partial_amount"); }}
            className={`w-full text-left p-4 rounded-xl border-2 transition-all cursor-pointer ${mode === "partial_amount" ? "border-blue-500 bg-blue-50/50" : "border-slate-100 hover:border-slate-200"}`}
            data-testid="q-convert-mode-partial"
          >
            <p className="font-semibold text-slate-900 text-sm">Milestone / Partial Invoice</p>
            <p className="text-xs text-slate-500 mt-0.5">Bill a slice now (e.g. advance, phase 1). The quote stays open so you can invoice the rest later.</p>
            {mode === "partial_amount" && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3" onClick={e => e.stopPropagation()}>
                <div>
                  <label className="block text-[11px] text-slate-500 mb-1">Amount (incl. tax)</label>
                  <Input
                    type="number"
                    value={amount}
                    onChange={e => setAmount(e.target.value)}
                    placeholder={`Max ${formatCurrencyINR(remaining)}`}
                    data-testid="q-convert-amount"
                  />
                </div>
                <div>
                  <label className="block text-[11px] text-slate-500 mb-1">Label (optional)</label>
                  <Input
                    value={label}
                    onChange={e => setLabel(e.target.value)}
                    placeholder="e.g. Milestone 1 — Advance"
                    data-testid="q-convert-label"
                  />
                </div>
                <div className="sm:col-span-2 flex flex-wrap gap-1.5 mt-1">
                  {[25, 50, 75, 100].map(p => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setAmount(String(Math.round(remaining * p / 100 * 100) / 100))}
                      className="text-[11px] px-2 py-0.5 rounded-md border border-slate-200 hover:bg-slate-50 text-slate-600"
                      data-testid={`q-convert-pct-${p}`}
                    >
                      {p}%
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={handleConvert}
            disabled={submitting}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="q-convert-confirm"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />}
            Create Invoice
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
}

function Metric({ label, value, testid, tone = "slate" }) {
  const toneCls = {
    slate: "text-slate-900",
    blue: "text-blue-700",
    emerald: "text-emerald-700",
    amber: "text-amber-700",
    violet: "text-violet-700",
  }[tone] || "text-slate-900";
  return (
    <div className="bg-slate-50 rounded-xl px-2 py-2.5" data-testid={testid}>
      <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">{label}</p>
      <p className={`text-sm sm:text-base font-bold mt-0.5 ${toneCls} truncate`}>{value}</p>
    </div>
  );
}
